import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * Shared website-link discovery.
 *
 * Both the "Add link" crawl tab and the one-click "Check for updates" dialog
 * ask the backend for the same read-only diff and receive the same
 * find_new_links_result / find_new_links_error WebSocket messages. This hook
 * owns that whole dance once: socket lifecycle, init frame, request_id
 * matching, the 5-minute client timeout, the three buckets and their
 * selections.
 *
 * It deliberately opens its own short-lived socket rather than reusing
 * LiveNotificationsProvider, matching the behaviour this replaced.
 */

const DISCOVERY_TIMEOUT_MS = 300000; // 5 min, matches the backend crawl budget

const useLinkDiscovery = ({
  botId,
  existingLinks = [],
  selectMissingByDefault = true,
  autoExpandFirst = true,
}) => {
  const { t } = useTranslation();

  const [isFetching, setIsFetching] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [rateLimited, setRateLimited] = useState(false);
  const [hasResult, setHasResult] = useState(false);

  const [newLinks, setNewLinks] = useState([]);
  const [existing, setExisting] = useState([]);
  const [missing, setMissing] = useState([]);
  const [selectedNew, setSelectedNew] = useState([]);
  const [selectedExisting, setSelectedExisting] = useState([]);
  const [selectedMissing, setSelectedMissing] = useState([]);
  const [expandedPanel, setExpandedPanel] = useState(null);
  const [removalBlocked, setRemovalBlocked] = useState(false);
  const [bases, setBases] = useState([]);

  const socketRef = useRef(null);
  const requestIdRef = useRef(null);
  const timeoutRef = useRef(null);
  // Read inside the socket callback, which is created once per request: a ref
  // keeps the scraping-filter honest even if the bot refreshes mid-crawl.
  const existingLinksRef = useRef(existingLinks);
  existingLinksRef.current = existingLinks;
  const selectMissingRef = useRef(selectMissingByDefault);
  selectMissingRef.current = selectMissingByDefault;
  const autoExpandRef = useRef(autoExpandFirst);
  autoExpandRef.current = autoExpandFirst;

  const clearTimer = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const closeSocket = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.onopen = null;
      socketRef.current.onerror = null;
      socketRef.current.onmessage = null;
      socketRef.current.onclose = null;
      try { socketRef.current.close(); } catch {}
      socketRef.current = null;
    }
  }, []);

  const clearBuckets = useCallback(() => {
    setNewLinks([]);
    setExisting([]);
    setMissing([]);
    setSelectedNew([]);
    setSelectedExisting([]);
    setSelectedMissing([]);
    setExpandedPanel(null);
    setRemovalBlocked(false);
  }, []);

  const reset = useCallback(() => {
    clearBuckets();
    setIsFetching(false);
    setErrorMsg("");
    setRateLimited(false);
    setHasResult(false);
    setBases([]);
    requestIdRef.current = null;
    clearTimer();
    closeSocket();
  }, [clearBuckets, closeSocket]);

  useEffect(() => () => {
    clearTimer();
    if (socketRef.current) {
      try { socketRef.current.close(); } catch {}
      socketRef.current = null;
    }
  }, []);

  const openSocketAndInit = () =>
    new Promise((resolve, reject) => {
      try {
        closeSocket();
        const ws = new WebSocket(process.env.REACT_APP_API_WEBSOCKET_URL);
        socketRef.current = ws;
        ws.onopen = () => {
          ws.send(JSON.stringify({
            type: "init",
            user_id: localStorage.getItem("user_id"),
            token: localStorage.getItem("token"),
          }));
          resolve();
        };
        ws.onerror = () => {
          if (socketRef.current === ws) socketRef.current = null;
          reject(new Error(t("ws_connection_failed")));
        };
        ws.onmessage = (event) => {
          try {
            const msg = JSON.parse(event.data);
            if (!msg || msg.bot_id !== botId) return;
            if (msg.request_id && requestIdRef.current && msg.request_id !== requestIdRef.current) return;

            if (msg.type === "find_new_links_error") {
              setIsFetching(false);
              setErrorMsg(msg.message || t("generic_error_occurred"));
              clearBuckets();
              try { ws.close(); } catch {}
              if (socketRef.current === ws) socketRef.current = null;
              requestIdRef.current = null;
              clearTimer();
              return;
            }

            if (msg.type === "find_new_links_result" ||
                (Array.isArray(msg.new_found_links) && Array.isArray(msg.already_existing_links))) {
              const nf = msg.new_found_links || [];
              const ex = msg.already_existing_links || [];
              const ne = msg.links_not_existing_anymore || [];

              // Links mid-scrape cannot be re-actioned, so they never show up
              // as "already trained".
              const scrapingUrls = new Set(
                (existingLinksRef.current || []).filter((l) => l.status === "scraping").map((l) => l.url_link)
              );
              const exFiltered = ex.filter((url) => !scrapingUrls.has(url));

              setNewLinks(nf);
              setExisting(exFiltered);
              setMissing(ne);
              setSelectedNew(nf);
              setSelectedExisting(exFiltered);
              // Removal is destructive and irreversible, so the caller decides
              // whether it starts ticked.
              setSelectedMissing(selectMissingRef.current ? ne : []);
              setRemovalBlocked(!!msg.removal_blocked);
              if (Array.isArray(msg.bases)) setBases(msg.bases);
              setIsFetching(false);
              setHasResult(true);

              if (nf.length === 0 && exFiltered.length === 0 && ne.length === 0) {
                setErrorMsg(t("no_links_found_at_url"));
              } else {
                setErrorMsg("");
              }
              // The update dialog keeps every bucket collapsed so the summary
              // stays three lines and one button; the add modal opens the first
              // populated one, as it always did.
              setExpandedPanel(
                !autoExpandRef.current
                  ? null
                  : nf.length > 0 ? "new" : exFiltered.length > 0 ? "existing" : ne.length > 0 ? "missing" : null
              );

              try { ws.close(); } catch {}
              if (socketRef.current === ws) socketRef.current = null;
              requestIdRef.current = null;
              clearTimer();
            }
          } catch {
            setIsFetching(false);
            setErrorMsg(t("malformed_message_error"));
            try { ws.close(); } catch {}
            if (socketRef.current === ws) socketRef.current = null;
            requestIdRef.current = null;
            clearTimer();
          }
        };
        ws.onclose = () => {
          if (socketRef.current === ws) socketRef.current = null;
        };
      } catch {
        setErrorMsg(t("ws_setup_failed_error"));
        reject(new Error("ws_setup_failed"));
      }
    });

  /**
   * Kick off a discovery run. `request` performs the POST and may throw; it
   * receives the generated request_id. Everything else (socket, timeout,
   * failure cleanup) is identical for both entry points.
   */
  const run = useCallback(async (request) => {
    try {
      setErrorMsg("");
      setRateLimited(false);
      setHasResult(false);
      setIsFetching(true);
      const reqId = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      requestIdRef.current = reqId;
      await openSocketAndInit();
      await request(reqId);
      clearTimer();
      timeoutRef.current = setTimeout(() => {
        setIsFetching(false);
        setErrorMsg(t("request_timeout_error"));
        requestIdRef.current = null;
        closeSocket();
      }, DISCOVERY_TIMEOUT_MS);
    } catch (e) {
      setIsFetching(false);
      if (e?.rateLimited) {
        setRateLimited(true);
        setErrorMsg("");
      } else {
        setErrorMsg(e?.message || t("link_discovery_failed"));
      }
      requestIdRef.current = null;
      clearTimer();
      closeSocket();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [botId, closeSocket, t]);

  /** Crawl one URL the customer typed (Add-link modal, crawl tab). */
  const startCrawl = useCallback((url) => {
    if (!url?.trim()) {
      setErrorMsg(t("enter_valid_url"));
      return Promise.resolve();
    }
    return run(async (reqId) => {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}find_new_links`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ bot_id: botId, url, request_id: reqId }),
      });
      if (!res.ok) {
        let text = "";
        try { text = await res.text(); } catch {}
        throw new Error(text || t("request_failed_status", { status: res.status }));
      }
    });
  }, [botId, run, t]);

  /** Check the whole bot: the backend resolves what to crawl from the bot itself. */
  const startUpdateCheck = useCallback(() => run(async (reqId) => {
    const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}knowledge/update_check/${botId}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ request_id: reqId }),
    });
    // Checking is not throttled per bot. A 429 here can only be the endpoint's
    // plain abuse limiter, so it is a "try again shortly", not a feature limit.
    if (res.status === 429) {
      const err = new Error("rate_limited");
      err.rateLimited = true;
      throw err;
    }
    if (res.status === 400) {
      throw new Error(t("update_check_no_base_url"));
    }
    if (!res.ok) {
      let text = "";
      try { text = await res.text(); } catch {}
      throw new Error(text || t("request_failed_status", { status: res.status }));
    }
    try {
      const body = await res.json();
      if (Array.isArray(body?.bases)) setBases(body.bases);
    } catch {}
  }), [botId, run, t]);

  const toggleLink = (setter) => (link) => (e) => {
    if (e.target.checked) setter((prev) => [...prev, link]);
    else setter((prev) => prev.filter((l) => l !== link));
  };

  const selectAll = (setter, all) => (e) => {
    if (e.target.checked) setter(all);
    else setter([]);
  };

  const toggleExpand = (panel, hasData) => {
    if (!hasData) return;
    setExpandedPanel((prev) => (prev === panel ? null : panel));
  };

  return {
    // status
    isFetching,
    errorMsg,
    setErrorMsg,
    rateLimited,
    hasResult,
    removalBlocked,
    bases,
    // buckets
    newLinks,
    existing,
    missing,
    selectedNew,
    selectedExisting,
    selectedMissing,
    setSelectedNew,
    setSelectedExisting,
    setSelectedMissing,
    // interaction helpers
    toggleNewLink: toggleLink(setSelectedNew),
    toggleExistingLink: toggleLink(setSelectedExisting),
    toggleMissingLink: toggleLink(setSelectedMissing),
    selectAllNew: selectAll(setSelectedNew, newLinks),
    selectAllExisting: selectAll(setSelectedExisting, existing),
    selectAllMissing: selectAll(setSelectedMissing, missing),
    expandedPanel,
    setExpandedPanel,
    toggleExpand,
    // lifecycle
    startCrawl,
    startUpdateCheck,
    reset,
    closeSocket,
  };
};

export default useLinkDiscovery;
